// List pages keep their place: filters, sort and search ride the URL as a
// query on the section route (#/deals?tab=held&sort=hotScore.desc&stages=loi),
// and the scroll offset is remembered per list URL in sessionStorage. Open a
// record, press Back, and the list comes back as it was left — and the URL is
// a link to exactly that view.
//
// The router already treats '#/<section>?…' as a refinement it never
// clobbers (router.jsx writeHash), so a list owns its own query. Each page
// writes with replaceState: changing a filter shouldn't pile up Back entries.
//
// Pure apart from window.location/history/sessionStorage, so the encode and
// decode rules run under node --test (test/list_state.test.mjs).
(function () {
  // Keys the list itself owns; a filter of the same name never round-trips.
  const RESERVED = new Set(['tab', 'scope', 'sort', 'q', 'layout', 'type']);
  const SCROLL_PREFIX = 'atlas:list-scroll:';
  const URL_PREFIX = 'atlas:list-url:';

  const store = () => {
    try { return window.sessionStorage || null; } catch (_) { return null; }
  };
  const sameValue = (a, b) => JSON.stringify(a) === JSON.stringify(b);
  const isSet = (v) => (window.filterValueActive
    ? window.filterValueActive(v)
    : v != null && v !== '' && !(Array.isArray(v) && !v.length));
  const parseObject = (raw) => {
    try { const v = JSON.parse(raw); return v && typeof v === 'object' && !Array.isArray(v) ? v : null; } catch (_) { return null; }
  };

  // { key: value } → URLSearchParams string, keeping only what differs from
  // `defaults`. Arrays repeat the key (?stages=a&stages=b), booleans are 1/0,
  // objects (a score range, an ad-hoc column filter) are JSON. A key absent
  // from `defaults` is written whenever it is set — the Filter popover's
  // "any column" filters (col:<key>) only exist once someone adds one.
  const encodeFilters = (filters, defaults, qs) => {
    const out = qs || new URLSearchParams();
    for (const [k, v] of Object.entries(filters || {})) {
      if (RESERVED.has(k)) continue;
      const d = defaults ? defaults[k] : undefined;
      if (d !== undefined ? sameValue(v, d) : !isSet(v)) continue;
      if (Array.isArray(v)) {
        if (!v.length) out.append(k, '');            // cleared a non-empty default
        else v.forEach((x) => out.append(k, String(x)));
      } else if (typeof v === 'boolean') {
        out.set(k, v ? '1' : '0');
      } else if (v && typeof v === 'object') {
        out.set(k, JSON.stringify(v));
      } else {
        out.set(k, v == null ? '' : String(v));
      }
    }
    return out;
  };

  // The inverse: read each key back in the shape its default has. Unknown
  // keys are only taken when they parse as an object (col:* filters); a
  // stray param from somewhere else never becomes a filter.
  const decodeFilters = (qs, defaults) => {
    const out = {};
    const seen = new Set();
    for (const k of qs.keys()) {
      if (seen.has(k) || RESERVED.has(k)) continue;
      seen.add(k);
      const d = defaults ? defaults[k] : undefined;
      const raw = qs.get(k);
      if (Array.isArray(d)) {
        out[k] = qs.getAll(k).filter((x) => x !== '');
      } else if (typeof d === 'boolean') {
        out[k] = raw === '1' || raw === 'true';
      } else if (d === undefined || (d && typeof d === 'object')) {
        const v = parseObject(raw);
        if (v) out[k] = v;
      } else {
        // A '' default can hold an object once set (the hot-score range).
        out[k] = (raw && raw[0] === '{' && parseObject(raw)) || raw;
      }
    }
    return out;
  };

  // The full list state → query string. `sort` is { key, dir }; pass
  // defaultSort so the page's own default order stays out of the URL.
  const encode = ({ tab, scope, sort, q, layout, type, filters }, d = {}) => {
    const qs = new URLSearchParams();
    if (tab && tab !== d.tab) qs.set('tab', tab);
    if (scope && scope !== d.scope) qs.set('scope', scope);
    if (sort && sort.key && !(d.sort && d.sort.key === sort.key && d.sort.dir === sort.dir)) {
      qs.set('sort', `${sort.key}.${sort.dir === 'asc' ? 'asc' : 'desc'}`);
    }
    if (q) qs.set('q', q);
    if (layout && layout !== d.layout) qs.set('layout', layout);
    if (type && type !== d.type) qs.set('type', type);
    encodeFilters(filters, d.filters, qs);
    return qs.toString();
  };

  const decode = (query, d = {}) => {
    const qs = new URLSearchParams(query || '');
    const out = {};
    if (qs.has('tab')) out.tab = qs.get('tab');
    if (qs.has('scope')) out.scope = qs.get('scope');
    const s = qs.get('sort');
    if (s) {
      const i = s.lastIndexOf('.');
      const key = i > 0 ? s.slice(0, i) : s;
      const dir = i > 0 ? s.slice(i + 1) : 'desc';
      out.sort = { key, dir: dir === 'asc' ? 'asc' : 'desc' };
    }
    if (qs.get('q')) out.q = qs.get('q');
    if (qs.has('layout')) out.layout = qs.get('layout');
    if (qs.has('type')) out.type = qs.get('type');
    const f = decodeFilters(qs, d.filters);
    if (Object.keys(f).length) out.filters = f;
    return out;
  };

  // The query on '#/<section>?…', or null when the hash is any other route
  // (a record page, another section, no hash). '' = the bare list.
  const queryOf = (section, hash) => {
    const h = hash == null ? (window.location.hash || '') : hash;
    const base = '#/' + section;
    if (h === base) return '';
    if (h.startsWith(base + '?')) return h.slice(base.length + 1);
    return null;
  };

  // replaceState the list's query in place — only while the hash is still
  // this section's (a list effect can fire after navigation has moved on).
  // Returns the hash now in the bar, or null when nothing was ours to write.
  const write = (section, query) => {
    if (queryOf(section) == null) return null;
    const next = '#/' + section + (query ? '?' + query : '');
    if ((window.location.hash || '') !== next) {
      try { window.history.replaceState(window.history.state, '', next); } catch (_) { return null; }
    }
    const s = store();
    try { if (s) s.setItem(URL_PREFIX + section, next); } catch (_) {}
    return next;
  };

  // The list URL last written for `section` this tab — where a record page's
  // breadcrumb ("Engagements") goes back to.
  const lastUrl = (section) => {
    const s = store();
    try { return (s && s.getItem(URL_PREFIX + section)) || null; } catch (_) { return null; }
  };

  // Navigate to the list as it was left. False when there's nothing saved,
  // so the caller falls back to its plain section switch.
  const returnTo = (section) => {
    const url = lastUrl(section);
    if (!url || queryOf(section, url) == null) return false;
    try { window.location.hash = url.slice(1); } catch (_) { return false; }
    return true;
  };

  // Strip one param from whatever list URL is in the bar (any section), in
  // place. The header's All-teams toggle drops `scope` before it reloads:
  // the toggle re-decides scope, and a link still saying scope=mine would
  // beat it.
  const dropParam = (key) => {
    const h = window.location.hash || '';
    const m = /^#\/([^/?#]+)\?(.*)$/.exec(h);
    if (!m) return false;
    const qs = new URLSearchParams(m[2]);
    if (!qs.has(key)) return false;
    qs.delete(key);
    const rest = qs.toString();
    try { window.history.replaceState(window.history.state, '', '#/' + m[1] + (rest ? '?' + rest : '')); } catch (_) { return false; }
    return true;
  };

  // Scroll offset + how many rows were loaded, per list URL. `count` lets the
  // page fetch enough rows up front for the offset to land on them.
  const saveScroll = (url, top, count) => {
    const s = store();
    if (!s || !url) return;
    try { s.setItem(SCROLL_PREFIX + url, JSON.stringify({ top: Math.max(0, Math.round(top || 0)), count: count || 0 })); } catch (_) {}
  };
  // Read once: a later fresh visit to the same URL starts at the top.
  const takeScroll = (url) => {
    const s = store();
    if (!s || !url) return null;
    try {
      const raw = s.getItem(SCROLL_PREFIX + url);
      if (!raw) return null;
      s.removeItem(SCROLL_PREFIX + url);
      const v = JSON.parse(raw);
      return v && typeof v.top === 'number' ? v : null;
    } catch (_) { return null; }
  };

  window.AtlasListState = {
    encode, decode, encodeFilters, decodeFilters, queryOf, write, lastUrl, returnTo, dropParam, saveScroll, takeScroll,
  };
})();
